import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useMyOrganizationsQuery } from "@newHooks/query/myOrganization/myOrganizations.query";
import { useMyInfoContext } from "../myInfo/myInfo.context";
import { MyOrganizationWithMembers } from "@maven-rest/auth/Organization.dto";
import { compareBigintSafe, findBigintSafe } from "@maven-helper/functions/Misc.function";
import dayjs from "dayjs";

type OrganizationContextType = {
  organizations: MyOrganizationWithMembers[];
  organization: MyOrganizationWithMembers | undefined;
  isLoading: boolean;
  isOrganizationOwner: boolean;
  changeOrganization: (organizationId: string) => void;
};

export function getOrganizationLocalStorageKey(userId: string): string {
  return `DOCS:${userId}:ORG`;
}

export function changeCurrentOrganization(key: string, organizationId: string): void {
  localStorage.setItem(key, organizationId);
}

const OrganizationContext = createContext<OrganizationContextType>({
  organizations: [],
  organization: undefined,
  isLoading: false,
  isOrganizationOwner: false,
  changeOrganization: () => {},
});

type OrganizationProviderProps = {
  children: React.ReactNode;
};

function OrganizationProvider({ children }: OrganizationProviderProps) {
  const { myInfo } = useMyInfoContext();
  const userId = myInfo?.id.toString();

  const [organization, setOrganization] = useState<MyOrganizationWithMembers>();

  const { data, isLoading } = useMyOrganizationsQuery({
    select: (data) => ({
      ...data,
      organizations: data.organizations.sort((a, b) => dayjs(b.createdAt).diff(dayjs(a.createdAt))),
    }),
  });

  const organizations = useMemo(() => data?.organizations || [], [data]);

  useEffect(() => {
    if (!organizations.length || !userId) return;

    const organizationId = localStorage.getItem(getOrganizationLocalStorageKey(userId));
    const foundOrganization = organizationId && findBigintSafe(organizations, "id", BigInt(organizationId));

    setOrganization(foundOrganization || organizations[0]);
  }, [organizations, userId]);

  const changeOrganization = (organizationId: string) => {
    if (!userId) return;

    changeCurrentOrganization(getOrganizationLocalStorageKey(userId), organizationId);
    setOrganization(findBigintSafe(organizations, "id", BigInt(organizationId)) || organizations[0]);
  };

  return (
    <OrganizationContext.Provider
      value={{
        organizations,
        organization,
        isLoading,
        isOrganizationOwner: compareBigintSafe(organization?.ownerUserId, userId),
        changeOrganization,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
}

function useOrganizationContext() {
  return useContext(OrganizationContext);
}

export { OrganizationContext, OrganizationProvider, useOrganizationContext };
